import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import { Box, Button, Link, Stack, TextField, Typography } from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import { T } from "../../../lib/types/common";
import { Messages } from "../../../lib/config";
import { LoginInput, MemberInput } from "../../../lib/types/member";
import MemberService from "../../services/MemberService";
import { sweetErrorHandling } from "../../../lib/sweetAlert";
import { useGlobals } from "../../hooks/useGlobals";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    outline: "none",
  },
  paper: {
    position: "relative",
    width: "640px",
    maxWidth: "95vw",
    borderRadius: 22,
    overflow: "hidden",
    background:
      "radial-gradient(900px 220px at 10% 0%, rgba(55,118,204,0.16), rgba(255,255,255,0) 60%), linear-gradient(180deg, rgba(238,244,255,0.66) 0%, rgba(255,255,255,0.90) 100%)",
    border: "1px solid rgba(23,115,207,0.12)",
    boxShadow: "0px 10px 30px rgba(17,24,39,0.08)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      background:
        "radial-gradient(circle at 10% 0%, rgba(55,118,204,0.10), rgba(0,0,0,0) 55%), radial-gradient(circle at 90% 10%, rgba(215,182,134,0.08), rgba(0,0,0,0) 52%)",
      pointerEvents: "none",
    },
  },
  content: {
    position: "relative",
    flex: 1,
    padding: "34px 44px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    textAlign: "left",
    [theme.breakpoints.down("sm")]: {
      padding: "26px 18px",
    },
  },
  title: {
    fontWeight: 800,
    color: "#0f172a",
    fontSize: 26,
    lineHeight: 1.2,
    letterSpacing: "-0.02em",
  },
  subtitle: {
    marginTop: 8,
    color: "rgba(15,23,42,0.65)",
    fontSize: 13,
  },
}));

interface AuthenticationModalProps {
  signupOpen: boolean;
  loginOpen: boolean;
  handleSignupClose: () => void;
  handleLoginClose: () => void;
  handleSwitchFromSignupToLogin: () => void;
}

export default function AuthenticationModal(props: AuthenticationModalProps) {
  const {
    signupOpen,
    loginOpen,
    handleSignupClose,
    handleLoginClose,
    handleSwitchFromSignupToLogin,
  } = props;
  const classes = useStyles();
  const [memberNick, setMemberNick] = useState<string>("");
  const [memberPhone, setMemberPhone] = useState<string>("");
  const [memberPassword, setMemberPassword] = useState<string>("");
  const { setAuthMember } = useGlobals();

  /** HANDLERS **/

  const handleUserName = (e: T) => {
    setMemberNick(e.target.value);
  };

  const handlePhone = (e: T) => {
    setMemberPhone(e.target.value);
  };

  const handlePassword = (e: T) => {
    setMemberPassword(e.target.value);
  };

  const handlePasswordKeyDown = (e: T) => {
    if (e.key === "Enter" && signupOpen) {
      handleSignupRequest().then();
    } else if (e.key === "Enter" && loginOpen) {
      handleLoginRequest().then();
    }
  };

  const handleSignupRequest = async () => {
    try {
      const isFullfill = memberNick !== "" && memberPhone !== "" && memberPassword !== "";
      if (!isFullfill) throw new Error(Messages.error3);

      const signupInput: MemberInput = {
        memberNick: memberNick,
        memberPassword: memberPassword,
        memberPhone: memberPhone,
      };

      const member = new MemberService();
      const result = await member.signup(signupInput);

      // Saving authenticated user
      setAuthMember(result);
      handleSignupClose();
    } catch (err) {
      handleSignupClose();
      sweetErrorHandling(err).then();
    }
  };

  const handleLoginRequest = async () => {
    try {
      const isFullfill = memberNick !== "" && memberPassword !== "";
      if (!isFullfill) throw new Error(Messages.error3);

      const loginInput: LoginInput = {
        memberNick: memberNick,
        memberPassword: memberPassword,
      };

      const member = new MemberService();
      const result = await member.login(loginInput);
      setAuthMember(result);

      // Saving authenticated user
      handleLoginClose();
    } catch (err) {
      handleLoginClose();
      sweetErrorHandling(err).then();
    }
  };

  const authTextFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.70)",
      borderRadius: "12px",
      "& fieldset": {
        borderColor: "rgba(15,23,42,0.10)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(15,23,42,0.15)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#6366f1",
      },
    },
    "& .MuiInputLabel-root": {
      color: "rgba(15,23,42,0.50)",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#a5b4fc",
    },
  } as const;

  return (
    <div>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modal}
        open={signupOpen}
        onClose={handleSignupClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          style: { backgroundColor: "rgba(0,0,0,0.255)" },
        }}
      >
        <Fade in={signupOpen}>
          <Stack
            className={classes.paper}
          >
            <Stack className={classes.content}>
              <Typography className={classes.title} component="h2">
                Create account
              </Typography>
              <Typography className={classes.subtitle}>
                Sign up in seconds
              </Typography>

              <Box
                sx={{
                  width: 78,
                  height: 3,
                  borderRadius: 999,
                  mt: 1.5,
                  mb: 0.5,
                  background:
                    "linear-gradient(90deg, rgba(23,115,207,0.85) 0%, rgba(23,115,207,0) 100%)",
                }}
              />

              <Stack spacing={2} sx={{ marginTop: "18px", width: "100%" }}>
                <TextField
                  sx={authTextFieldSx}
                  id="signup-username"
                  label="Username"
                  variant="outlined"
                  size="small"
                  value={memberNick}
                  onChange={handleUserName}
                  autoComplete="username"
                  fullWidth
                />

                <TextField
                  sx={authTextFieldSx}
                  id="signup-phone"
                  label="Phone number"
                  variant="outlined"
                  size="small"
                  type="tel"
                  value={memberPhone}
                  onChange={handlePhone}
                  autoComplete="tel"
                  fullWidth
                />

                <TextField
                  sx={authTextFieldSx}
                  id="signup-password"
                  label="Password"
                  variant="outlined"
                  size="small"
                  type="password"
                  value={memberPassword}
                  onChange={handlePassword}
                  onKeyDown={handlePasswordKeyDown}
                  autoComplete="new-password"
                  fullWidth
                />

                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleSignupRequest}
                  endIcon={<LoginIcon />}
                  sx={{
                    mt: "4px",
                    borderRadius: "999px",
                    textTransform: "none",
                    fontWeight: 700,
                    padding: "12px 18px",
                    boxShadow: "0 12px 30px rgba(37, 99, 235, 0.28)",
                    color: "#fff",
                    "& svg": { color: "#fff" },
                    background:
                      "linear-gradient(135deg, rgba(37,99,235,1) 0%, rgba(29,78,216,1) 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, rgba(37,99,235,0.96) 0%, rgba(29,78,216,0.96) 100%)",
                      boxShadow: "0 16px 40px rgba(37, 99, 235, 0.34)",
                    },
                  }}
                  fullWidth
                >
                  Signup
                </Button>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 1.5,
                    width: "100%",
                    textAlign: "center",
                    color: "rgba(15,23,42,0.65)",
                  }}
                >
                  Already have an account?{" "}
                  <Link
                    component="button"
                    type="button"
                    onClick={handleSwitchFromSignupToLogin}
                    sx={{
                      fontWeight: 700,
                      verticalAlign: "baseline",
                      cursor: "pointer",
                      color: "#2563eb",
                      border: "none",
                      background: "none",
                      padding: 0,
                      font: "inherit",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Log in
                  </Link>
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Fade>
      </Modal>

      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modal}
        open={loginOpen}
        onClose={handleLoginClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          style: { backgroundColor: "rgba(0,0,0,0.55)" },
        }}
      >
        <Fade in={loginOpen}>
          <Stack
            className={classes.paper}
          >
            <Stack className={classes.content}>
              <Typography className={classes.title} component="h2">
                Welcome back
              </Typography>
              <Typography className={classes.subtitle}>
                Login to continue.
              </Typography>

              <Box
                sx={{
                  width: 78,
                  height: 3,
                  borderRadius: 999,
                  mt: 1.5,
                  mb: 0.5,
                  background:
                    "linear-gradient(90deg, rgba(23,115,207,0.85) 0%, rgba(23,115,207,0) 100%)",
                }}
              />

              <Stack spacing={2} sx={{ marginTop: "18px", width: "100%" }}>
                <TextField
                  sx={authTextFieldSx}
                  id="login-username"
                  label="Username"
                  variant="outlined"
                  size="small"
                  value={memberNick}
                  onChange={handleUserName}
                  autoComplete="username"
                  fullWidth
                />

                <TextField
                  sx={authTextFieldSx}
                  id="login-password"
                  label="Password"
                  variant="outlined"
                  size="small"
                  type="password"
                  value={memberPassword}
                  onChange={handlePassword}
                  onKeyDown={handlePasswordKeyDown}
                  autoComplete="current-password"
                  fullWidth
                />

                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleLoginRequest}
                  endIcon={<LoginIcon />}
                  sx={{
                    mt: "4px",
                    borderRadius: "999px",
                    textTransform: "none",
                    fontWeight: 700,
                    padding: "12px 18px",
                    boxShadow: "0 12px 30px rgba(37, 99, 235, 0.28)",
                    color: "#fff",
                    "& svg": { color: "#fff" },
                    background:
                      "linear-gradient(135deg, rgba(37,99,235,1) 0%, rgba(29,78,216,1) 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, rgba(37,99,235,0.96) 0%, rgba(29,78,216,0.96) 100%)",
                      boxShadow: "0 16px 40px rgba(37, 99, 235, 0.34)",
                    },
                  }}
                  fullWidth
                >
                  Login
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Fade>
      </Modal>
    </div>
  );
}
